import { Clipboard } from '@angular/cdk/clipboard';
import { CommonModule, DOCUMENT } from '@angular/common';
import { Component, effect, ElementRef, inject, input, OnDestroy, output, Renderer2, ViewChild } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { Router, RouterModule } from '@angular/router';
import { BrowserStorageService } from '@core/services/browser-storage.service';
import { provideIcons } from '@ng-icons/core';
import { heroAdjustmentsVertical, heroBanknotes } from '@ng-icons/heroicons/outline';
import { TranslateModule } from '@ngx-translate/core';
import { constants } from '@shared/constants/constants';
import { debounceTime, Subject, takeUntil } from 'rxjs';
import { ZoomDetectionService } from '../../services/zoom-detection.service';
import { RouteSearchComponent } from '../route-search/route-search.component';
import { BOTTOM_NAVIGATION_LIST, ISidebarItem, SIDEBAR_NAVIGATION_LIST, SidebarInfoBubbleKey } from './sidebar.helper';

@Component({
  selector: 'app-sidebar',
  templateUrl: './sidebar.component.html',
  imports: [CommonModule, RouterModule, TranslateModule, ReactiveFormsModule, MatIconModule, RouteSearchComponent],
  providers: [
    provideIcons({
      heroAdjustmentsVertical,
      heroBanknotes,
    }),
  ],
  styleUrl: './sidebar.component.scss',
})
export class SidebarComponent implements OnDestroy {
  router = inject(Router);
  private clipboard = inject(Clipboard);
  private document = inject(DOCUMENT);
  private renderer = inject(Renderer2);
  private zoomDetectionService = inject(ZoomDetectionService);
  private storageService = inject(BrowserStorageService);
  private readonly infoBubbleShownStorageKeys: Record<SidebarInfoBubbleKey, string> = {
    referral: 'referralInfoBubbleShown',
    directDebit: 'directDebitInfoBubbleShown',
  };
  private readonly infoBubbleAutoOpenDelay = 1000;
  // Grace period so the pointer can travel from the trigger to the bubble without closing it.
  private readonly infoBubbleHideDelay = 200;
  // Only one bubble is ever open at a time - first available wins.
  private readonly infoBubblePriority: SidebarInfoBubbleKey[] = ['referral', 'directDebit'];
  readonly open = input<boolean>(false);
  readonly userName = input<string>('');
  readonly userMail = input<string>('');
  readonly onlyProperties = input<boolean>(false);
  readonly referral_url = input<string>('');
  readonly onboarded = input<boolean>(false);
  readonly showDirectDebitInfo = input<boolean>(false);
  readonly sidebarClosed = output<void>();
  public readonly sidebarNavigationList: ISidebarItem[] = SIDEBAR_NAVIGATION_LIST;
  public readonly bottomNavigationList: ISidebarItem[] = BOTTOM_NAVIGATION_LIST;
  public readonly showSummerCampaign = constants.showReferralSummerCampaign;
  public greeting = '';
  public openInfoBubble: SidebarInfoBubbleKey | null = null;
  public form: FormGroup = new FormGroup({
    search: new FormControl(null),
  });
  public showCopyConfirmation = false;
  private pinnedInfoBubble: SidebarInfoBubbleKey | null = null;
  private infoBubbleElement: HTMLElement | null = null;
  private infoBubbleAutoOpenTimer?: ReturnType<typeof setTimeout>;
  private infoBubbleHideTimer?: ReturnType<typeof setTimeout>;
  private destroy$ = new Subject<void>();

  @ViewChild('referralInfoButton') private referralInfoButton?: ElementRef<HTMLElement>;
  @ViewChild('directDebitInfoButton') private directDebitInfoButton?: ElementRef<HTMLElement>;
  @ViewChild('infoBubble')
  private set infoBubble(element: ElementRef<HTMLElement> | undefined) {
    const triggerElement = this.getInfoBubbleTrigger();
    if (!element || !triggerElement) {
      return;
    }

    this.infoBubbleElement = element.nativeElement;
    this.renderer.appendChild(this.document.body, this.infoBubbleElement);
    this.positionInfoBubble();
    this.zoomDetectionService.attachRepositionListeners({
      triggerElement,
      overlayElement: this.infoBubbleElement,
      onReposition: () => this.positionInfoBubble(),
    });
  }

  constructor() {
    this.greeting = this.getGreeting();
    this._formSubscription();
    effect(() => {
      const autoOpenKey = this.resolveAutoOpenInfoBubble();
      if (autoOpenKey) {
        this.scheduleInfoBubbleAutoOpen(autoOpenKey);
        return;
      }

      this.cancelInfoBubbleAutoOpen();
      const openKey = this.openInfoBubble;
      if (openKey && (!this.isSidebarVisible() || !this.isInfoBubbleAvailable(openKey))) {
        this.closeInfoBubble();
      }
    });
  }

  closeSidebar() {
    this.sidebarClosed.emit();
  }

  public getGreeting(): string {
    const currentHour = new Date().getHours();
    if (currentHour < 12) {
      return 'GREETINGS.MORNING';
    } else if (currentHour < 18) {
      return 'GREETINGS.AFTERNOON';
    } else {
      return 'GREETINGS.EVENING';
    }
  }

  ngOnDestroy(): void {
    this.cancelInfoBubbleAutoOpen();
    this.cancelInfoBubbleHide();
    this.detachInfoBubble();
    this.destroy$.next();
    this.destroy$.complete();
  }

  private _formSubscription(): void {
    this.form
      .get('search')
      ?.valueChanges.pipe(debounceTime(500))
      .pipe(takeUntil(this.destroy$))
      .subscribe(value => {
        console.log(value);
      });
  }

  copyReferralLink() {
    if (this.referral_url) {
      this.clipboard.copy(this.referral_url());
      this.showCopyConfirmation = true;
      setTimeout(() => {
        this.showCopyConfirmation = false;
      }, 1500);
    }
  }

  isInfoBubbleAvailable(key: SidebarInfoBubbleKey | undefined): boolean {
    switch (key) {
      case 'referral':
        return this.showSummerCampaign && this.onboarded();
      case 'directDebit':
        return this.showDirectDebitInfo();
      default:
        return false;
    }
  }

  isInfoBubbleOpen(key: SidebarInfoBubbleKey): boolean {
    return this.openInfoBubble === key;
  }

  isInfoBubbleVisible(key: SidebarInfoBubbleKey): boolean {
    return this.openInfoBubble === key && this.isInfoBubbleAvailable(key);
  }

  showInfoBubble(key: SidebarInfoBubbleKey | undefined): void {
    if (!key || !this.isInfoBubbleAvailable(key)) {
      return;
    }

    this.cancelInfoBubbleHide();
    if (this.openInfoBubble === key) {
      return;
    }

    this.cancelInfoBubbleAutoOpen();
    this.detachInfoBubble();
    this.openInfoBubble = key;
  }

  hideInfoBubble(key: SidebarInfoBubbleKey | undefined): void {
    if (!key || this.openInfoBubble !== key || this.pinnedInfoBubble === key) {
      return;
    }

    this.cancelInfoBubbleHide();
    this.infoBubbleHideTimer = setTimeout(() => {
      this.infoBubbleHideTimer = undefined;
      if (this.openInfoBubble === key && this.pinnedInfoBubble !== key) {
        this.closeInfoBubble();
      }
    }, this.infoBubbleHideDelay);
  }

  toggleInfoBubble(key: SidebarInfoBubbleKey, event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.cancelInfoBubbleAutoOpen();
    this.cancelInfoBubbleHide();
    if (this.pinnedInfoBubble === key) {
      this.closeInfoBubble();
      this.markInfoBubbleAsShown(key);
      return;
    }

    this.detachInfoBubble();
    this.pinnedInfoBubble = key;
    this.openInfoBubble = key;
  }

  dismissInfoBubble(key: SidebarInfoBubbleKey, event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.cancelInfoBubbleAutoOpen();
    this.closeInfoBubble();
    this.markInfoBubbleAsShown(key);
  }

  onNavigationClick() {
    this.closeInfoBubble();
    this.closeSidebar();
  }

  private getInfoBubbleTrigger(): HTMLElement | null {
    switch (this.openInfoBubble) {
      case 'referral':
        return this.referralInfoButton?.nativeElement ?? null;
      case 'directDebit':
        return this.directDebitInfoButton?.nativeElement ?? null;
      default:
        return null;
    }
  }

  private positionInfoBubble(): void {
    const triggerElement = this.getInfoBubbleTrigger();
    if (!triggerElement || !this.infoBubbleElement) {
      return;
    }

    const position = this.zoomDetectionService.getTooltipPosition(triggerElement, this.infoBubbleElement, 'top');

    this.renderer.setStyle(this.infoBubbleElement, 'position', 'absolute');
    this.renderer.setStyle(this.infoBubbleElement, 'top', `${position.top}px`);
    this.renderer.setStyle(this.infoBubbleElement, 'left', `${position.left}px`);
    this.renderer.setStyle(this.infoBubbleElement, 'z-index', '1200');
  }

  private scheduleInfoBubbleAutoOpen(key: SidebarInfoBubbleKey): void {
    if (this.infoBubbleAutoOpenTimer) {
      return;
    }

    this.infoBubbleAutoOpenTimer = setTimeout(() => {
      this.infoBubbleAutoOpenTimer = undefined;
      if (this.resolveAutoOpenInfoBubble() !== key) {
        return;
      }

      this.openInfoBubble = key;
      this.pinnedInfoBubble = key;
      this.markInfoBubbleAsShown(key);
    }, this.infoBubbleAutoOpenDelay);
  }

  private cancelInfoBubbleAutoOpen(): void {
    if (!this.infoBubbleAutoOpenTimer) {
      return;
    }

    clearTimeout(this.infoBubbleAutoOpenTimer);
    this.infoBubbleAutoOpenTimer = undefined;
  }

  private cancelInfoBubbleHide(): void {
    if (!this.infoBubbleHideTimer) {
      return;
    }

    clearTimeout(this.infoBubbleHideTimer);
    this.infoBubbleHideTimer = undefined;
  }

  private closeInfoBubble(): void {
    this.cancelInfoBubbleHide();
    this.openInfoBubble = null;
    this.pinnedInfoBubble = null;
    this.detachInfoBubble();
  }

  private detachInfoBubble(): void {
    if (this.infoBubbleElement) {
      this.zoomDetectionService.detachRepositionListeners(this.infoBubbleElement);
      this.infoBubbleElement = null;
    }
  }

  private hasInfoBubbleBeenShown(key: SidebarInfoBubbleKey): boolean {
    return this.storageService.getItem(this.infoBubbleShownStorageKeys[key]) === 'true';
  }

  private markInfoBubbleAsShown(key: SidebarInfoBubbleKey): void {
    this.storageService.setItem(this.infoBubbleShownStorageKeys[key], 'true');
  }

  private resolveAutoOpenInfoBubble(): SidebarInfoBubbleKey | null {
    const candidates = this.infoBubblePriority.filter(
      key => this.isInfoBubbleAvailable(key) && !this.hasInfoBubbleBeenShown(key)
    );

    if (!this.isSidebarVisible()) {
      return null;
    }

    return candidates[0] ?? null;
  }

  private isSidebarVisible(): boolean {
    return this.open() || !this.isTabletDownViewport();
  }

  private isTabletDownViewport(): boolean {
    if (typeof window === 'undefined') {
      return false;
    }

    return window.matchMedia('(max-width: 1024px), (max-width: 1366px) and (max-height: 1024px) and (hover: none)')
      .matches;
  }
}
